import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AdminInput, AdminSelect } from '@project/components';
import CompanySelect from './../CompanySelect/CompanySelect';
import PropertySelect from './../PropertySelect/PropertySelect';
import SpaceSelect from './../SpaceSelect/SpaceSelect';
import axios, { axiosV2 } from '../../utils/axios';
import AdminLogout from '../AdminLogout/AdminLogout';
import MenuToggle from './../Shared/MenuToggle/MenuToggle';
import { useIntl } from 'react-intl';
import styles from './CreateNewProfile.module.css';
import { IoMdInformationCircle } from 'react-icons/io';
import { RootContext } from '../../hoc/RootContext/RootContext';

function CreateNewProfile() {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const { mobileView } = useContext(RootContext);

  const navigate = useNavigate();

  const [selectedCompany, setSelectedCompany] = useState(undefined);
  const [selectedProperty, setSelectedProperty] = useState(undefined);
  const [selectedSpace, setSelectedSpace] = useState(undefined);


  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  const [idpRecordRequired, setIDPRecordRequired] = useState(false);

  const [availableCrms, setAvailableCrms] = useState([]);
  const [selectedIdpOption, setSelectedIdpOption] = useState(undefined);

  const [idpRecordId, setIdpRecordId] = useState('');
  const [customIdpRecordId, setCustomIdpRecordId] = useState('');

  useEffect(() => {
    if (!selectedCompany || !selectedProperty) {
      setAvailableCrms([]);
      return;
    }
    const fetchData = async () => {
      try {
        const params = new URLSearchParams();
        params.set('page', '1');
        params.append('limit', '100');
        if (selectedProperty.idpRecordId) {
          params.append('propertyIdpRecordId', selectedProperty.idpRecordId) 
        }
        const result = await axiosV2.get(`/id360/crms/company/${encodeURIComponent(selectedCompany.value)}?${params.toString()}`)
        const crms = result.data.data || [];
        setAvailableCrms(crms.filter(crm => !crm.spaceProfileGuid));
      } catch (e) {
        setAvailableCrms([]);
        console.error(e);
      }
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, selectedProperty]);

  const crmOptions = useMemo(() => {
    return [
        ...availableCrms.map(crm => ({
        value: crm.id360IdpRecordId,
        label: crm.id360IdpRecordId + (crm.unit ? ` — ${crm.unit}` : ''),
        crm
      })),
      { value: '__other__', label: t('CreateNewProfile.idpRecord.other') }
    ]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCrms])
  const canCreateProfile = !!(selectedCompany && selectedProperty && selectedSpace && idpRecordId);

  const handleCreateProfile = async (e) => {
    if (!canCreateProfile) return;
    if (!idpRecordId) {
      setIDPRecordRequired(true);
      return;
    }
    setIDPRecordRequired(false);

    e.preventDefault();
    setErrors(null);

    try {
      setIsSaving(true);

      const result = await axios.post('/space-profiles', {
        company: selectedCompany.value,
        property: selectedProperty.value,
        address1: selectedProperty.address1,
        address2: selectedSpace.value,
        city: selectedProperty.city,
        state: selectedProperty.state,
        postal: selectedProperty.postal,
        asset_guid: selectedSpace.assetGuid,
        latitude: selectedProperty.latitude,
        longitude: selectedProperty.longitude,
        basic_info: {
          integrations: {
            idp_record_id: idpRecordId !== '__other__' ? idpRecordId : customIdpRecordId
          }
        },
        idp_record_id: idpRecordId !== '__other__' ? idpRecordId : customIdpRecordId
      });

      if (result.status === 200 && selectedIdpOption.value !== '__other__') {
        try {
          await axiosV2.patch('/id360/crms/assign-space-profile-guid', {
            id360IdpRecordId: idpRecordId,
            spaceProfileGuid: result.data.record.guid
          });
        } catch (err) {
          setErrors('Failed to link CRM record to Space Profile.');
          setIsSaving(false);
          return;
        }
        setIsSaving(false);
        navigate(`/admin/profile/edit/${result.data.record.guid}`);
      } else if (result.status === 200 && selectedIdpOption.value === '__other__') {
        setIsSaving(false);
        navigate(`/admin/profile/edit/${result.data.record.guid}`);
      } else {
        setErrors(`${t('error.general')}: ${result.errors}`);
      }
    } catch (e) {
      setIsSaving(false);
      if (e.response && e.response.data) {
        setErrors([`${t('error.spaceProfile.create')} (${e.response.data.errors})`]);
      } else {
        setErrors(t('error.server'));
        console.error(e);
      }
    }
  }

  return (
    <section>
      <header>
        <div className='d-flex'>
          <MenuToggle />
          <h3 className='is-page-title ml-3'>{t('CreateNewProfile.title')}</h3>
        </div>
        <div>
          <AdminLogout />
        </div>
      </header>
      <main>
        <h2>{t('CreateNewProfile.subtitle')}</h2>
        <Form className='is-common-form'>
          <div className='row my-5'>
            <div className='col-sm-6'>
              <CompanySelect selectedCompany={selectedCompany} setSelectedCompany={setSelectedCompany} />
            </div>
            <div className='col-sm-6'>
              <PropertySelect selectedCompany={selectedCompany} setSelectedProperty={setSelectedProperty} selectedProperty={selectedProperty} />
            </div>
          </div>
          <div className='row my-5'>
            <div className='col-sm-6'>
              <AdminInput label={t('term.address')} placeholder={t('term.address')} value={selectedProperty ? selectedProperty.address || t('CreateNewProfile.addressNotFound') : ""} readOnly={true} customstyle={styles.address} />
            </div>
            <div className='col-sm-6'>
              <SpaceSelect
                selectedCompany={selectedCompany}
                selectedProperty={selectedProperty}
                selectedSpace={selectedSpace}
                setSelectedSpace={setSelectedSpace}
              />
            </div>
          </div>
          <div className='row my-5'>
            <div className='col-sm-6'>
              <div className="d-flex justify-content-center align-items-center position-relative w-100">
                {(() => {
                  return (
                    <>
                      <AdminSelect
                        id="idp-record-select"
                        label={<span>{t('term.idpRecord')} <span style={{ color: 'red' }}>*</span></span>}
                        placeholder={t('CreateNewProfile.selectIdpRecord')}
                        value={selectedIdpOption}
                        onChange={option => {
                          if (option && option.value === '__other__') {
                            setIdpRecordId('__other__');
                            setSelectedIdpOption(option);
                          } else {
                            setIdpRecordId(option ? option.value : '');
                            setSelectedIdpOption(option);
                            setCustomIdpRecordId('');
                          }
                          setIDPRecordRequired(false);
                        }}
                        isLoading={false}
                        required
                        options={crmOptions}
                        identifierClass={'data-idp-record-id w-100'}
                        noOptionsMessage={t('CreateNewProfile.selectIdpRecord')}
                        isInvalid={idpRecordRequired}
                        validationMessage={t('CreateNewProfile.idpRecord.required')}
                      />
                    </>
                  );
                })()}

                <div className={styles.infoIcon} onMouseOver={() => setShowInfo(true)} onMouseOut={() => setShowInfo(false)}>
                  <IoMdInformationCircle />
                  {!mobileView && showInfo &&
                    <div className={styles.infoPopup}>
                      {t('CreateNewProfile.idpRecord.details')}
                    </div>}
                </div>
                {mobileView && showInfo &&
                  <div className={styles.infoPopup}>
                    {t('CreateNewProfile.idpRecord.details')}
                  </div>}
              </div>
              {selectedIdpOption && selectedIdpOption.value === '__other__' && (
                <AdminInput
                  id="custom-idp-record"
                  label={t('CreateNewProfile.enterIdpRecord')}
                  type="text"
                  placeholder={t('CreateNewProfile.enterIdpRecord')}
                  value={customIdpRecordId}
                  onChange={e => setCustomIdpRecordId(e.target.value)}
                  required
                  customstyle="mt-2"
                />
              )}
            </div>
          </div>

          <div>
            {errors ? <Alert key={errors} variant='danger'>{t('error.general')}: {errors}</Alert> : null}
            {selectedSpace && selectedSpace.spaceStatus && selectedSpace.spaceProfileGuid ?
              <Button className='btn-prime' onClick={() => navigate(`/admin/profile/edit/${selectedSpace.spaceProfileGuid}`)}>{t('CreateNewProfile.editProfile')}</Button> :
              <Button className='btn-prime' onClick={handleCreateProfile} disabled={!canCreateProfile || isSaving}>{t('CreateNewProfile.createProfile.button')}</Button>}
          </div>
        </Form>
      </main>
    </section>
  )
}

export default CreateNewProfile;